// react
import { useState } from 'react';

// mui
import { Alert, Button, Stack, TextField } from '@mui/material';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon';

export interface VerifyAccessV2Props {
  onSubmit: (email: string) => void;
  onUseInviteKey: () => void;
  onPartnerCode?: (code: string) => Promise<boolean>;
  onRefreshAuth?: () => void;
  helperText?: string;
  inviteKeyName?: string;
  partnerCodeError?: string | null;
  isValidatingCode?: boolean;
  /** For Storybook: render partner code form directly */
  initialView?: 'email' | 'partner-code';
}

export const VerifyAccessV2 = ({
  onSubmit,
  onUseInviteKey,
  onPartnerCode,
  onRefreshAuth,
  inviteKeyName = 'Payroll ID',
  partnerCodeError,
  isValidatingCode = false,
  initialView = 'email',
}: VerifyAccessV2Props) => {
  const [email, setEmail] = useState('');
  const [showPartnerCode, setShowPartnerCode] = useState(
    initialView === 'partner-code',
  );
  const [partnerCode, setPartnerCode] = useState('');

  if (showPartnerCode) {
    return (
      <PageTemplate
        maxWidth='xs'
        headerSlot={
          <PageHeader
            title='Enter Organisation Code'
            description='Enter the organisation creation code provided by your partner.'
          />
        }
        contentSlot={
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (onPartnerCode && partnerCode.trim()) {
                await onPartnerCode(partnerCode.trim());
              }
            }}
          >
            <Stack
              sx={{
                gap: 2,
              }}
            >
              <TextField
                label='Organisation Creation Code'
                placeholder='e.g. TIME-001'
                variant='standard'
                size='medium'
                value={partnerCode}
                onChange={(e) => setPartnerCode(e.target.value)}
                helperText='This code was provided to you by your partner.'
                fullWidth
                disabled={isValidatingCode}
              />
              {partnerCodeError && (
                <Alert severity='error'>{partnerCodeError}</Alert>
              )}
              <Button
                aria-label='Validate Code'
                variant='contained'
                type='submit'
                disabled={!partnerCode.trim() || isValidatingCode}
              >
                {isValidatingCode ? 'Validating…' : 'Continue'}
              </Button>
              <Button
                aria-label='Back to email verification'
                variant='text'
                onClick={() => {
                  setShowPartnerCode(false);
                  setPartnerCode('');
                }}
                disabled={isValidatingCode}
              >
                Back
              </Button>
            </Stack>
          </form>
        }
      />
    );
  }

  return (
    <PageTemplate
      maxWidth='xs'
      headerSlot={
        <Stack
          direction='row'
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <PageHeader
            title='Verify Access'
            description='Please enter your work email address so we can confirm your company benefit.'
          />
          {onRefreshAuth && (
            <Button onClick={onRefreshAuth} data-testid='refresh-button'>
              <Icon icon='rotate-right' />
            </Button>
          )}
        </Stack>
      }
      contentSlot={
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(email);
          }}
        >
          <Stack
            sx={{
              gap: 2,
            }}
          >
            <TextField
              label='Work Email Address'
              placeholder='example@company.com'
              variant='standard'
              size='medium'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              helperText='Use the email address provided by your employer.'
              fullWidth
            />
            <Button aria-label='Continue' variant='contained' type='submit'>
              Continue
            </Button>
            <Button
              aria-label={`Use ${inviteKeyName} Instead`}
              variant='text'
              onClick={onUseInviteKey}
            >
              Use {inviteKeyName} Instead
            </Button>
            {onPartnerCode && (
              <Button
                aria-label='I have an organisation creation code'
                variant='text'
                onClick={() => setShowPartnerCode(true)}
              >
                I have a partner code
              </Button>
            )}
          </Stack>
        </form>
      }
    />
  );
};

VerifyAccessV2.displayName = 'VerifyAccessV2';
export default VerifyAccessV2;
